<template>
    <div class="add-product-container">
        <!-- 标题 -->
        <h2 class="page-title">添加商品</h2>

        <!-- 表单区域 -->
        <el-form ref="productForm" :model="form" :rules="rules" label-width="120px" class="product-form">
            <!-- 商品ID（自动生成，不可编辑） -->
            <el-form-item label="商品ID">
                <el-input v-model="form.shopid" disabled placeholder="系统自动生成" />
            </el-form-item>

            <!-- 商品名称 -->
            <el-form-item label="商品名称" prop="shopname">
                <el-input v-model="form.shopname" placeholder="请输入商品名称" clearable />
            </el-form-item>

            <!-- 商品价格（积分） -->
            <el-form-item label="商品价格" prop="shopprice">
                <el-input-number v-model="form.shopprice" :min="1" :max="999999" controls-position="right"
                    placeholder="请输入商品价格" />
            </el-form-item>

            <!-- 制作周期 -->
            <el-form-item label="制作周期" prop="shoptime">
                <el-input-number v-model="form.shoptime" :min="1" :max="90" controls-position="right"
                    placeholder="请输入制作周期（天）" /><a> 天</a>
            </el-form-item>

            <!-- 库存数量 -->
            <el-form-item label="库存数量" prop="stock">
                <div class="stock-field">
                    <el-switch v-model="stockUnlimited" active-text="不限库存" inactive-text="限量库存"
                        @change="handleStockModeChange" />
                    <el-input-number v-if="!stockUnlimited" v-model="form.stock" :min="1" :max="9999"
                        :precision="0" :step="1" controls-position="right" class="stock-input"
                        placeholder="请输入库存数量" />
                    <span v-else class="stock-tip">不限库存时用户可无限次兑换</span>
                </div>
                <div class="stock-hint">限量库存可填 1-9999；售罄后前台自动显示「已售罄」并禁止兑换</div>
            </el-form-item>

            <!-- 商品状态 -->
            <el-form-item label="商品状态" prop="shopsale">
                <el-radio-group v-model="form.shopsale">
                    <el-radio label="1">可生产</el-radio>
                    <el-radio label="0">不可生产</el-radio>
                </el-radio-group>
            </el-form-item>

            <!-- 是否虚拟礼物 -->
            <el-form-item label="礼物类型" prop="virtual">
                <el-radio-group v-model="form.virtual">
                    <el-radio label="0">实物</el-radio>
                    <el-radio label="1">电子礼物</el-radio>
                </el-radio-group>
            </el-form-item>

            <!-- 电子礼物文件上传（最多3个） -->
            <el-form-item label="电子礼物文件" v-if="form.virtual === '1'" prop="virtualFiles">
                <div class="virtual-file-upload">
                    <el-upload
                        ref="virtualUploadRef"
                        :auto-upload="false"
                        :limit="3"
                        :on-change="handleVirtualFileChange"
                        :on-exceed="handleExceed"
                        :on-remove="handleVirtualFileRemove"
                        :file-list="virtualFileList"
                        multiple
                        drag
                    >
                        <el-icon class="el-icon--upload"><UploadFilled /></el-icon>
                        <div class="el-upload__text">
                            拖拽文件到此处或 <em>点击上传</em>
                        </div>
                        <template #tip>
                            <div class="el-upload__tip">
                                最多上传3个文件（格式不限），文件将作为电子礼物提供给用户下载（限5次），文件大小最好小于10MB
                            </div>
                        </template>
                    </el-upload>
                </div>
            </el-form-item>

            <!-- 商品图片（用户头像风格上传） -->
            <el-form-item label="商品图片" prop="shopimg">
                <div class="avatar-uploader">
                    <el-upload class="avatar-uploader-trigger" :action="uploadUrl" :show-file-list="false"
                        :on-success="handleAvatarSuccess" :before-upload="beforeAvatarUpload" :auto-upload="false"
                        :on-change="handleImageChange">
                        <img v-if="form.imagePreview" :src="form.imagePreview" class="avatar" />
                        <div v-else class="avatar-placeholder">
                            <i class="el-icon-plus avatar-placeholder-icon"></i>
                            <p class="avatar-placeholder-text">点击上传商品图片</p>
                        </div>
                    </el-upload>
                    <div class="upload-tip">支持 JPG/PNG 格式，建议尺寸 300×300px</div>
                </div>
            </el-form-item>

            <!-- 操作按钮 -->
            <el-form-item class="btn-group">
                <el-button type="primary" @click="handleSubmit">提交</el-button>
                <el-button @click="handleReset">重置</el-button>
            </el-form-item>
        </el-form>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElForm, ElFormItem, ElInput, ElInputNumber, ElRadioGroup, ElRadio, ElUpload, ElButton, ElImage, UploadFilled } from 'element-plus';
import axios from 'axios';

// 表单数据
// 库存：-1 表示不限库存；1-9999 表示限量库存（与后端约定一致）
const STOCK_UNLIMITED = -1;

const form = reactive({
    shopid: generateUUID(), // 自动生成ID
    shopname: '',
    shopprice: '',
    shoptime: '',
    shopsale: '1',
    stock: 10,          // 默认限量 10 件
    shopimg: null,
    imagePreview: '',
    virtual: '0'
});

// true = 不限库存（提交 -1），false = 限量库存（提交 1-9999）
const stockUnlimited = ref(false);

// 两个控件共用一个 form.stock：切到不限时先记住上次填的数量，切回限量时还原
let lastLimitedStock = 10;
const handleStockModeChange = (unlimited) => {
    if (unlimited) {
        if (form.stock > 0) lastLimitedStock = form.stock;
        form.stock = STOCK_UNLIMITED;
    } else {
        form.stock = lastLimitedStock > 0 ? lastLimitedStock : 10;
    }
};

const virtualUploadRef = ref(null);
const virtualFileList = ref([]);
const virtualRawFiles = ref([]);

// 表单校验规则
const rules = {
    shopname: [
        { required: true, message: '请输入商品名称', trigger: 'blur' },
        { min: 2, max: 50, message: '商品名称长度在2-50个字符之间', trigger: 'blur' }
    ],
    shopprice: [
        { required: true, message: '请输入商品价格', trigger: 'blur' },
        { type: 'number', min: 1, message: '商品价格必须大于0', trigger: 'blur' }
    ],
    shoptime: [
        { required: true, message: '请输入制作周期', trigger: 'blur' },
        { type: 'number', min: 1, max: 90, message: '制作周期应在1-90天内', trigger: 'blur' }
    ],
    stock: [
        {
            // 不限库存（-1）不校验范围；限量库存必须是 1-9999 的整数
            validator: (rule, value, callback) => {
                if (stockUnlimited.value) return callback();
                const n = Number(value);
                if (!Number.isInteger(n) || n < 1 || n > 9999) {
                    return callback(new Error('库存数量需为 1-9999 的整数，或选择不限库存'));
                }
                callback();
            },
            trigger: 'blur'
        }
    ],
};

// 表单引用
const productForm = ref(null);
// 上传URL（示例）
const uploadUrl = 'https://example.com/upload';

// 生成唯一ID
function generateUUID() {
    return 'SP' + Date.now().toString(36) + Math.random().toString(36).substr(2, 5).toUpperCase();
}

// 图片上传处理
const handleImageChange = (file) => {
    const isImage = ['image/jpeg', 'image/png'].includes(file.raw.type);
    if (!isImage) {
        ElMessage.error('仅支持上传JPG/PNG格式的图片');
        return false;
    }

    const isLt2M = file.raw.size / 1024 / 1024 < 10;
    if (!isLt2M) {
        ElMessage.error('图片大小不能超过10MB');
        return false;
    }

    // 显示预览
    const reader = new FileReader();
    reader.onload = (e) => {
        form.imagePreview = e.target.result;
    };
    reader.readAsDataURL(file.raw);
    form.shopimg = file.raw;
};

// 上传成功处理（示例）
const handleAvatarSuccess = (res, file) => {
    if (res.code === 200) {
        form.imagePreview = res.url;
        ElMessage.success('图片上传成功');
    } else {
        ElMessage.error(res.message || '上传失败');
    }
};

// 虚拟文件处理
const handleVirtualFileChange = (file) => {
    virtualRawFiles.value.push(file.raw);
};

const handleVirtualFileRemove = (file) => {
    const index = virtualRawFiles.value.findIndex(f => f.name === file.name && f.size === file.size);
    if (index > -1) virtualRawFiles.value.splice(index, 1);
};

const handleExceed = () => {
    ElMessage.warning('最多只能上传3个文件');
};

// 上传前校验
const beforeAvatarUpload = (file) => {
    const isJPG = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJPG) {
        ElMessage.error('请上传JPG/PNG格式的图片');
        return false;
    }

    const isLt2M = file.size / 1024 / 1024 < 10;
    if (!isLt2M) {
        ElMessage.error('图片大小不能超过10MB');
        return false;
    }

    return true;
};

// 提交表单
const handleSubmit = () => {
    productForm.value.validate((valid) => {
        if (valid) {
            const formData = new FormData();
            formData.append('shopid', form.shopid);
            formData.append('shopname', form.shopname);
            formData.append('shopprice', form.shopprice);
            formData.append('shoptime', form.shoptime);
            formData.append('shopsale', form.shopsale);
            formData.append('virtual', form.virtual);
            // -1 = 不限库存
            formData.append('stock', stockUnlimited.value ? STOCK_UNLIMITED : form.stock);
            
            // 确保图片数据被添加
            if (form.shopimg) {
                formData.append('file', form.shopimg);
            }
            
            // 添加虚拟礼物文件
            if (form.virtual === '1' && virtualRawFiles.value.length > 0) {
                virtualRawFiles.value.forEach(f => {
                    formData.append('virtualFiles', f);
                });
            }
            
            axios.post('/adminapi/shop/add', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            }).then(res => {
                // 按后端返回码判断成功，避免失败也提示成功
                if (res.data && res.data.code === 200) {
                    ElMessage.success('商品添加成功！');
                    handleReset();
                } else {
                    ElMessage.error((res.data && res.data.message) || '商品添加失败！');
                }
            }).catch(err => {
                ElMessage.error('添加失败: ' + err.message);
            });
        } else {
            ElMessage.error('表单校验失败，请检查填写内容');
        }
    });
};

// 重置表单
const handleReset = () => {
    productForm.value.resetFields();
    form.shopid = generateUUID(); // 重新生成ID
    form.imagePreview = '';
    form.shopimg = null;
    form.virtual = '0';
    // resetFields 只还原到初始值，这里显式复原库存（含开关状态）
    stockUnlimited.value = false;
    lastLimitedStock = 10;
    form.stock = 10;
    // 电子礼物文件列表也要清空，否则上一件商品的文件会被带到下一件
    virtualFileList.value = [];
    virtualRawFiles.value = [];
};
</script>

<style scoped>
.add-product-container {
    padding: 20px;
    background-color: #fff;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    max-width: 600px;
    margin: 0 auto;
}

.page-title {
    font-size: 20px;
    font-weight: bold;
    margin-bottom: 20px;
    color: #303133;
}

.avatar-uploader {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.avatar-uploader-trigger {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    border: 1px dashed #d9d9d9;
    cursor: pointer;
    overflow: hidden;
    background-color: #f7f8fa;
    transition: border-color 0.3s;

    &:hover {
        border-color: #409EFF;
    }
}

.avatar {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.avatar-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
}

.avatar-placeholder-icon {
    font-size: 28px;
    color: #999;
    margin-bottom: 5px;
}

.avatar-placeholder-text {
    font-size: 12px;
    color: #999;
    text-align: center;
    max-width: 80px;
}

.upload-tip {
    margin-top: 10px;
    font-size: 12px;
    color: #999;
}

.stock-field {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

.stock-input {
    width: 160px;
}

.stock-tip {
    font-size: 13px;
    color: #909399;
}

.stock-hint {
    width: 100%;
    margin-top: 4px;
    font-size: 12px;
    color: #909399;
    line-height: 1.5;
}

.btn-group {
    display: flex;
    gap: 10px;
    margin-top: 20px;
}
</style>